// Customizable Area Start
import React, {useRef, useEffect, useState, useCallback} from "react";
import Container from "@material-ui/core/Container";
import { styled } from "@material-ui/core/styles";
import { Box, Grid, Theme, createStyles, makeStyles } from "@material-ui/core";
import CreateNewPost from "./CreateNewPost.web";
import Post from "./Post.web";
import { DashboardAccount, PostType } from "./interfaces";
import CustomLoader from "../../../components/src/CustomLoader.web";

const PostWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  "& > div": {
    scrollSnapAlign: "start",
    minHeight: "100vh",
    "@media (min-width: 650px)": {
      scrollSnapAlign: "none",
      minHeight: "auto",
    },
    "@media (max-width: 650px)": {
      marginBottom: "0px", // Remove margin between posts on mobile
      paddingBottom: "0px", // Remove padding between posts on mobile
      scrollSnapAlign: "start",
      height: "100vh", // Use consistent vh unit
      minHeight: "100vh",
      width: "100%", // Ensure full width
      maxWidth: "100%", // Prevent overflow
    }
  }
});

interface Props {
  pageNo: number
  postLoader: boolean
  totalPages: number
  posts: PostType[];
  showAlert: boolean;
  showSuccess: string;
  userAccount?: DashboardAccount
  isSortingComments: boolean
  banUserApiCall: (values: any) => void;
  hidePostApicall: (values: any) => void;
  deletePostApicall?: (postId: string) => void;
  errorAlertMessage: boolean;
  handlePageNoChange: (pageNo: number) => void
  postCommentApiCall: (values: any, postId?: string) => void;
  postPollApiCall: (values: any) => void;
  deleteCommentApiCall: (id: number, postId: string) => void;
  archiveCommentApiCall: (values: any) => void;
  revertArchiveCommentApiCall: (id: number) => void;
  getPostCommentsApiCall: (postId: string) => void;
  handleSortingCommentsChange: (isSorting: boolean) => void
  handleLikePostAPICall: (data: any, likeType: string) => void;
  handleUndoLikeAPICall: (likeableId: string, likeType: string, postId?: string) => void;
  achiveId: 0;
  scrollToPost: number;
  handlePostProfileClick: (account_id: number) => void
  handleCreatePostRedirection: () => void
  handleVote: (optionId: number, postId: string) => void
  handleDeleteVote: (optionId: number, postId: string) => void
  handleVoteComment: (optionId: number, commentId: number) => void
  notificationData?: any;
  isCommentModalOpen?: boolean;
  setCommentModalOpen?: (isOpen: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      gap: theme.spacing(3),
      [theme.breakpoints.up("md")]: {
        margin: theme.spacing(3, 0),
      },
      "@media (max-width: 650px)": {
        gap: "0px", // Remove gap on mobile
        margin: "0px", // Remove margin on mobile
        // Prevent mobile viewport issues
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100vw",
        overflowX: "hidden",
      }
    },
    allPosts: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      margin: "auto",
      scrollSnapType: "y mandatory",
      overflowY: "auto",
      // Use more reliable viewport calculations
      height: "calc(100vh - 120px)", // Fixed calculation: 100vh - header(56px) - tabbar(64px)
      minHeight: "100vh", // Fallback for older browsers
      scrollBehavior: "auto", // Instant scrolling on non-mobile
      overscrollBehaviorY: 'contain', // prevent blank space bounce past content ends
      paddingTop:'88px',
      "@media (min-width: 1025px)": {
        alignItems: "normal",
        gap: "24px",
        scrollSnapType: "none",
        height: "auto",
        overflowY: "visible",
        scrollBehavior: "auto",
      },
      "@media (min-width: 651px) and (max-width: 1024px)": {
        alignItems: "normal",
        gap: "20px",
        scrollSnapType: "none",
        height: "auto",
        overflowY: "visible",
        scrollBehavior: "auto",
        maxWidth: "100%",
        width: "100%",
        paddingTop: "20px",
      },
      "@media (max-width: 650px)": {
        maxWidth: "100vw", // Full viewport width on mobile
        width: "100vw", // Ensure full width
        gap: "0px", // Remove gap between posts on mobile
        paddingBottom: "0px", // Remove bottom padding
        height: "calc(100vh - 120px)", // Consistent calculation
        minHeight: "100vh", // Fallback for older browsers
        scrollSnapType: "y mandatory", // Enable snap scrolling
        scrollBehavior: "smooth", // Smooth scrolling on mobile
        overscrollBehaviorY: 'contain',
        // Ensure proper mobile sizing
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
      }
    },
    contantFeedContainer: {
      padding: "0 24px 0 0",
      overflow: "hidden",
      position: "relative",
      [theme.breakpoints.down(1150)]: {
        padding: "0 24px"
      },
      [theme.breakpoints.down(1024)]: {
        padding: "0 16px",
        maxWidth: "100%",
      },
      [theme.breakpoints.down(768)]: {
        padding: "0 12px",
        maxWidth: "100%",
      },
      [theme.breakpoints.down(650)]: {
        padding: "0px",
        marginBottom: "0px", // Remove bottom margin on mobile
        paddingBottom: "0px", // Remove bottom padding on mobile
        width: "100vw", // Full viewport width
        maxWidth: "100vw", // Prevent overflow
        marginLeft: "0px",
        marginRight: "0px",
        // Ensure container takes full width
        position: "relative",
        left: "50%",
        right: "50%",
        transform: "translateX(-50%)",
      }
    },
    createPostContainer: {
      position: "fixed",
      backgroundColor: "#F1F5F9",
      width: "62.4%",
      zIndex: 999,
      [theme.breakpoints.down(1670)]: {
        width: "61.4%",
        maxWidth: "100%",
        paddingLeft: "24px",
        paddingRight: "24px",
      },
      
      [theme.breakpoints.down(1495)]: {
        width: "59.4%",
        maxWidth: "100%",
        paddingLeft: "24px",
        paddingRight: "24px",
      },
      [theme.breakpoints.down(1418)]: {
        width: "58%",
        maxWidth: "100%",
        paddingLeft: "24px",
        paddingRight: "24px",
      },
    
      [theme.breakpoints.down(1282)]: {
        position: "static", // 👈 remove fixed position on tablet
        width: "100%",
        maxWidth: "100%",
        paddingLeft: "24px",
        paddingRight: "24px",
      },
    
      [theme.breakpoints.down(768)]: {
        position: "static", // keep it removed for mobile too
        width: "100%",
        maxWidth: "100%",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    
      [theme.breakpoints.down(650)]: {
        position: "static",
        paddingLeft: "12px",
        paddingRight: "12px",
        marginTop: "0px",
        width: "100%",
        maxWidth: "100%",
      },
    },
    
    postItem: {
      width: "100%",
      maxWidth: "100%",
      marginBottom: "0px",
      paddingBottom: "0px",
      [theme.breakpoints.down(1024)]: {
        width: "100%",
        maxWidth: "100%",
        marginLeft: "0px",
        marginRight: "0px",
      },
      [theme.breakpoints.down(650)]: {
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "0px",
        marginRight: "0px",
        // Ensure full width on mobile
        position: "relative",
        left: "50%",
        right: "50%",
        transform: "translateX(-50%)",
      }
    }
  })
);

function ContentFeed({
  posts,
  pageNo,
  achiveId,
  showAlert,
  postLoader,
  totalPages,
  userAccount,
  showSuccess,
  isSortingComments,
  errorAlertMessage,
  hidePostApicall,
  deletePostApicall,
  banUserApiCall,
  handlePageNoChange,
  postCommentApiCall,
  postPollApiCall,
  deleteCommentApiCall,
  getPostCommentsApiCall,
  handleLikePostAPICall,
  handleUndoLikeAPICall,
  archiveCommentApiCall,
  handleCreatePostRedirection,
  handleSortingCommentsChange,
  revertArchiveCommentApiCall,
  scrollToPost,
  handlePostProfileClick,
  handleVote,
  handleDeleteVote,
  handleVoteComment,
  notificationData,
  isCommentModalOpen,
  setCommentModalOpen
}: Props) {
  const classes = useStyles();  
  const thresholdRef = useRef<HTMLDivElement | null>(null)
  const listContainerRef:any = useRef(null);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const isFirstPageLoading = pageNo === 1 && postLoader

  // Detect Android Chrome to apply JS-driven smooth scrolling (Chrome on Android can ignore CSS smooth behavior)
  const isAndroidChrome = typeof navigator !== 'undefined'
    ? /Android/i.test(navigator.userAgent) && /Chrome\/\d+/i.test(navigator.userAgent)
    : false;

  // Easing function for smoother feel similar to native reels
  const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  // Animate container scrollTop to a target
  const animateContainerScrollTo = useCallback((container: HTMLElement, targetTop: number, duration = 320) => {
    const startTop = container.scrollTop;
    const distance = targetTop - startTop;
    if (Math.abs(distance) < 1) return;
    const startTime = performance.now();
    let rafId: number;
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutCubic(progress);
      container.scrollTop = startTop + distance * eased;
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Center an element within the container using JS animation on Android Chrome, fallback to smooth scrollIntoView elsewhere
  const centerElementInContainer = useCallback((container: HTMLElement, element: HTMLElement) => {
    if (!container || !element) return;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const currentTop = container.scrollTop;
    const elementCenterOffset = (elementRect.top - containerRect.top) + (elementRect.height / 2);
    const containerCenter = containerRect.height / 2;
    const deltaToCenter = elementCenterOffset - containerCenter;
    const targetTop = currentTop + deltaToCenter;
    if (isAndroidChrome) {
      animateContainerScrollTo(container, targetTop, 340);
    } else {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [animateContainerScrollTo, isAndroidChrome]);

  const scrollElementToTopWithOffset = (element: Element, behavior: ScrollBehavior = 'auto') => {
    try {
      const headerVar = getComputedStyle(document.documentElement).getPropertyValue('--app-header-height') || '56px';
      const headerHeight = parseInt(headerVar.toString().replace('px', '')) || 56;
      const top = (element as HTMLElement).getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, left: 0, behavior });
    } catch (_) {
      (element as HTMLElement).scrollIntoView({ behavior, block: 'start' });
    }
  }

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    
    if (listContainerRef.current) {
      listContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
    
    setTimeout(() => {
      const createPostSection = document.querySelector('[data-test-id="create-post-field"]') || 
                               document.querySelector('input[placeholder="Create New Post"]');
      if (createPostSection) {
        scrollElementToTopWithOffset(createPostSection, 'auto');
      }
    }, 100);
  }, []);

  const resetToHomeView = useCallback(() => {
    setIsFullScreenMode(false);
    
    setCurrentPostIndex(0);
    
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    
    if (listContainerRef.current) {
      listContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
    
    const scrollableElements = document.querySelectorAll('[style*="overflow"], [class*="scroll"]');
    scrollableElements.forEach(element => {
      if (element.scrollTop > 0) {
        element.scrollTop = 0;
      }
    });
    
    setTimeout(() => {
      const createPostSection = document.querySelector('[data-test-id="create-post-field"]') || 
                               document.querySelector('input[placeholder="Create New Post"]');
      if (createPostSection) {
        scrollElementToTopWithOffset(createPostSection, 'auto');
      }
    }, 50);
    
    setTimeout(() => {
      const createPostSection = document.querySelector('[data-test-id="create-post-field"]') || 
                               document.querySelector('input[placeholder="Create New Post"]');
      if (createPostSection) {
        const parent = createPostSection.closest('.createPostContainer') || createPostSection.parentElement;
        if (parent instanceof HTMLElement) {
          parent.style.transition = 'all 0.3s ease';
          parent.style.backgroundColor = '#f0f9ff';
          parent.style.borderRadius = '8px';
          parent.style.padding = '8px';
          
          setTimeout(() => {
            parent.style.backgroundColor = 'transparent';
            parent.style.borderRadius = '0px';
            parent.style.padding = '0px';
          }, 2000);
        }
      }
    }, 300);
  }, []);

  const forceScrollToTop = useCallback(() => {
    setCurrentPostIndex(0);
    setIsFullScreenMode(false);
    
    window.scrollTo(0, 0);
    
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
    
    const scrollableElements = document.querySelectorAll('[style*="overflow"], [class*="scroll"]');
    scrollableElements.forEach(element => {
      if (element.scrollTop > 0) {
        element.scrollTop = 0;
      }
    });
    
    setTimeout(() => {
      const createPostInput = document.querySelector('input[placeholder="Create New Post"]') ||
                             document.querySelector('[data-test-id="create-post-field"]');
      
      if (createPostInput) {
        createPostInput.scrollIntoView({ 
          behavior: 'auto',
          block: 'start'
        });
      } else {
        const createPostContainer = document.querySelector('[class*="createPostContainer"]') ||
                                  document.querySelector('[class*="createPost"]') ||
                                  document.querySelector('form');
        
        if (createPostContainer) {
          createPostContainer.scrollIntoView({ 
            behavior: 'auto',
            block: 'start'
          });
        }
      }
    }, 50);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 200);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).scrollReelsToTop = scrollToTop;
      (window as any).resetToHomeView = resetToHomeView;
      (window as any).forceScrollToTop = forceScrollToTop;
      
      (window as any).handleHomeFromReels = () => {
        setCurrentPostIndex(0);
        setIsFullScreenMode(false);
        
        window.scrollTo(0, 0);
        
        if (listContainerRef.current) {
          listContainerRef.current.scrollTop = 0;
        }
        
        const scrollableElements = document.querySelectorAll('[style*="overflow"], [class*="scroll"]');
        scrollableElements.forEach(element => {
          if (element.scrollTop > 0) {
            element.scrollTop = 0;
          }
        });
        
        setTimeout(() => {
          const createPostInput = document.querySelector('input[placeholder="Create New Post"]') ||
                                 document.querySelector('[data-test-id="create-post-field"]');
          
          if (createPostInput) {
            scrollElementToTopWithOffset(createPostInput, 'auto');
          } else {
            const createPostContainer = document.querySelector('[class*="createPostContainer"]') ||
                                      document.querySelector('[class*="createPost"]') ||
                                      document.querySelector('form') ||
                                      document.querySelector('[class*="post"]');
            
            if (createPostContainer) {
              scrollElementToTopWithOffset(createPostContainer, 'auto');
            } else {
              window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
          }
        }, 50);
        
        setTimeout(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }, 200);
      };
    }
  }, [scrollToTop, resetToHomeView, forceScrollToTop]);
  
  // TikTok-style snap scrolling state
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [autoPlayVideos, setAutoPlayVideos] = useState(true);
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const hasInitialScrollRef = useRef(false);
  const initialScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewportStabilizedRef = useRef(false);
  const lastScrollTopRef = useRef(0);

  // Set highlighted post ID when notification data changes
  useEffect(() => {
    if (notificationData && notificationData.attributes) {
      const notificationPostId = notificationData.attributes.post_id;
      console.log("Setting highlighted post ID from notification:", notificationPostId);
      setHighlightedPostId(notificationPostId);
      
      // Clear post highlight after 2.5 seconds
      const timer = setTimeout(() => {
        console.log("Clearing post highlight after 2.5 seconds");
        setHighlightedPostId(null);
      }, 2500);
      
      return () => clearTimeout(timer);
    }
  }, [notificationData]);

  // TikTok-style snap scrolling and auto-play functionality (mobile only)
  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    // Check if we're on mobile/tablet (screen width <= 650px)
    let isMobile = window.innerWidth <= 650;
    
    // Initialize scroll position reference
    lastScrollTopRef.current = container.scrollTop;
    
    // Handle window resize to update mobile/desktop behavior
    const handleResize = () => {
      isMobile = window.innerWidth <= 650;
    };
    
    window.addEventListener('resize', handleResize);
    
    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      // Don't handle TikTok scroll behavior if comment modal is open
      if (isCommentModalOpen) {
        return;
      }
      
      const scrollTop = container.scrollTop;
      const scrollDelta = Math.abs(scrollTop - lastScrollTopRef.current);
      lastScrollTopRef.current = scrollTop;
      
      // Track initial scroll to prevent skipping first post
      if (!hasInitialScrollRef.current && isMobile) {
        hasInitialScrollRef.current = true;
        viewportStabilizedRef.current = false;
        // Wait for viewport to stabilize after URL bar hides (usually ~500ms)
        if (initialScrollTimeoutRef.current) {
          clearTimeout(initialScrollTimeoutRef.current);
        }
        initialScrollTimeoutRef.current = setTimeout(() => {
          // After viewport stabilizes, allow normal scroll behavior
          viewportStabilizedRef.current = true;
        }, 800);
      }
      
      setIsScrolling(true);
      
      // Clear existing timeout
      clearTimeout(scrollTimeout);
      
      // Set new timeout for scroll end
      scrollTimeout = setTimeout(() => {
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;
        
        // Find which post is currently in the center of the viewport
        const postElements = container.querySelectorAll('[data-post-id]');
        let closestPostIndex = 0;
        let minDistance = Infinity;
        
        postElements.forEach((postElement: Element, index: number) => {
          const postRect = postElement.getBoundingClientRect();
          const postCenter = postRect.top + postRect.height / 2;
          const distance = Math.abs(containerCenter - postCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestPostIndex = index;
          }
        });
        
        // On initial scroll, prevent jumping to second post
        // Only update post index if:
        // 1. Viewport has stabilized (after URL bar hides) OR
        // 2. User has actually scrolled significantly (more than 200px) OR
        // 3. Scroll delta indicates intentional user scrolling (more than 100px change)
        if (isMobile && !viewportStabilizedRef.current) {
          // During viewport stabilization period, be very conservative
          // Only allow index change if user has scrolled significantly
          if (scrollTop < 200 && scrollDelta < 100) {
            // Keep at first post if scroll is minimal (likely just viewport resize from URL bar hiding)
            closestPostIndex = 0;
          }
        }
        
        // Additional protection: prevent jumping to last post when at first post
        // Only allow index change to last post if user has scrolled significantly
        if (isMobile && closestPostIndex === postElements.length - 1 && currentPostIndex === 0) {
          // If we're detecting a jump from first to last, check if it's intentional
          if (scrollTop < 200 && !viewportStabilizedRef.current) {
            // Keep at first post if scroll is minimal
            closestPostIndex = 0;
          }
        }
        
        setCurrentPostIndex(closestPostIndex);
        
        // Handle infinite loop on mobile when at boundaries
        // Only loop when user intentionally scrolls past boundaries, not on automatic scroll detection
        // Removed automatic loop-to-last when at top to prevent unwanted jumping on reload
        if (isMobile && postElements.length > 0) {
          // Auto-center posts on mobile when scrolling to first or last post
          // But skip auto-centering on initial scroll to prevent jumping past first post
          if ((closestPostIndex === 0 || closestPostIndex === postElements.length - 1) && hasInitialScrollRef.current) {
            const currentPost = postElements[closestPostIndex] as HTMLElement;
            if (currentPost) {
              // Check if the post is not properly centered
              const postRect = currentPost.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              const postCenter = postRect.top + postRect.height / 2;
              const containerCenter = containerRect.top + containerRect.height / 2;
              const centerOffset = Math.abs(postCenter - containerCenter);
              
              // If the post is not centered (offset > 50px), center it
              // But only if scroll position indicates intentional scrolling (not just viewport resize)
              const scrollTop = container.scrollTop;
              if (centerOffset > 50 && scrollTop > 50) {
                centerElementInContainer(container, currentPost);
              }
            }
          }
        }
        
        // Auto-play video in current post (only on mobile)
        if (autoPlayVideos && isMobile) {
          const currentPost = postElements[closestPostIndex] as HTMLElement;
          if (currentPost) {
            const video = currentPost.querySelector('video');
            if (video) {
              video.play().catch(console.error);
            }
          }
          
          // Pause all other videos
          postElements.forEach((postElement: Element, index: number) => {
            if (index !== closestPostIndex) {
              const video = postElement.querySelector('video');
              if (video) {
                video.pause();
              }
            }
          });
        }
        
        setIsScrolling(false);
      }, 150);
    };

    const handleWheel = (e: WheelEvent) => {
      // Don't handle TikTok scroll behavior if comment modal is open
      if (isCommentModalOpen) {
        return;
      }
      
      // Only apply snap scrolling on mobile devices
      if (!isMobile) {
        return; // Allow normal scrolling on desktop
      }
      
      e.preventDefault();
      
      if (isScrolling) return;
      
      const container = listContainerRef.current;
      if (!container) return;
      
      const postElements = container.querySelectorAll('[data-post-id]');
      if (postElements.length === 0) return;
      
      let newIndex = currentPostIndex;
      
      if (e.deltaY > 0) {
        // Scroll down - next post (loop to first if at last)
        // On initial scroll, only allow moving to next if scroll position indicates intentional scroll
        if (currentPostIndex === 0 && !hasInitialScrollRef.current) {
          const scrollTop = container.scrollTop;
          // Only allow moving to next post if user has scrolled significantly
          if (scrollTop < 100) {
            // Mark that initial scroll has happened, but don't jump to next post yet
            hasInitialScrollRef.current = true;
            return; // Prevent jumping on initial scroll
          }
        }
        
        // Mark that initial scroll has happened after first significant scroll
        if (!hasInitialScrollRef.current) {
          hasInitialScrollRef.current = true;
        }
        if (currentPostIndex !== postElements.length - 1) {
          newIndex = currentPostIndex + 1;
        } else {
          return; // at last post, do not loop
        }
      } else {
        // Scroll up - previous post (loop to last if at first)
        // But prevent looping if user is at top and hasn't scrolled significantly
        if (currentPostIndex === 0) {
          return; // at first post, do not loop
        } else {
          newIndex = currentPostIndex - 1;
        }
      }
      
      if (newIndex !== currentPostIndex) {
        setCurrentPostIndex(newIndex);
        const targetPost = postElements[newIndex] as HTMLElement;
        if (targetPost) {
          // Always center the post in viewport for mobile
          centerElementInContainer(container, targetPost);
        }
      }
    };

    // Touch event handlers for mobile swipe gestures
    let touchStartY = 0;
    let touchEndY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (!isMobile || isCommentModalOpen) return;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isMobile || isCommentModalOpen) return;
      touchEndY = e.changedTouches[0].clientY;
      handleSwipe();
    };

    const handleSwipe = () => {
      if (!isMobile || isCommentModalOpen) return;
      
      const swipeThreshold = 50; // Minimum distance for a swipe
      const swipeDistance = touchStartY - touchEndY;
      
      if (Math.abs(swipeDistance) > swipeThreshold) {
        const container = listContainerRef.current;
        if (!container) return;
        
        const postElements = container.querySelectorAll('[data-post-id]');
        if (postElements.length === 0) return;
        
        let newIndex = currentPostIndex;
        
        if (swipeDistance > 0) {
          // Swipe up - next post (loop to first if at last)
          // On initial scroll, only allow moving to next if scroll position indicates intentional scroll
          if (currentPostIndex === 0 && !hasInitialScrollRef.current) {
            const scrollTop = container.scrollTop;
            // Only allow moving to next post if user has scrolled significantly
            if (scrollTop < 100) {
              // Mark that initial scroll has happened, but don't jump to next post yet
              hasInitialScrollRef.current = true;
              return; // Prevent jumping on initial scroll
            }
          }
          
          // Mark that initial scroll has happened after first significant scroll
          if (!hasInitialScrollRef.current) {
            hasInitialScrollRef.current = true;
          }
          if (currentPostIndex !== postElements.length - 1) {
            newIndex = currentPostIndex + 1;
          } else {
            return; // at last post, do not loop
          }
        } else {
          // Swipe down - previous post (loop to last if at first)
          // But prevent looping if user is at top and hasn't scrolled significantly
          if (currentPostIndex === 0) {
            return; // at first post, do not loop
          } else {
            newIndex = currentPostIndex - 1;
          }
        }
        
        if (newIndex !== currentPostIndex) {
          setCurrentPostIndex(newIndex);
          const targetPost = postElements[newIndex] as HTMLElement;
          if (targetPost) {
            targetPost.scrollIntoView({ 
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
      }
    };

    // Add event listeners
    container.addEventListener('scroll', handleScroll);
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    // Initial auto-play (only on mobile)
    if (autoPlayVideos && posts.length > 0 && isMobile) {
      const firstPost = container.querySelector('[data-post-id="0"]');
      if (firstPost) {
        const video = firstPost.querySelector('video');
        if (video) {
          video.play().catch(console.error);
        }
      }
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
      clearTimeout(scrollTimeout);
      if (initialScrollTimeoutRef.current) {
        clearTimeout(initialScrollTimeoutRef.current);
      }
    };
  }, [currentPostIndex, autoPlayVideos, posts.length, isCommentModalOpen]);

  // Determine if this post should have comments auto-expanded and which comment to highlight
  const getPostNotificationProps = (post: PostType) => {
    if (!notificationData || !notificationData.attributes) {
      return { highlightedCommentId: undefined, autoExpandComments: false, highlightPost: false };
    }

    const notificationPostId = notificationData.attributes.post_id;
    const isNotificationPost = post.id === notificationPostId;
    
    console.log("Notification Debug:", {
      notificationData,
      notificationPostId,
      postId: post.id,
      isNotificationPost,
      highlightedPostId
    });
    
    if (isNotificationPost) {
      const isCommentNotification = notificationData.attributes.headings === "BxBlockComments::Comment";
      
      if (isCommentNotification) {
        // For comment notifications, auto-expand comments and highlight the specific comment
        // Extract comment ID from notification data
        const commentId = notificationData.attributes.comment_id || 
                         notificationData.attributes.commentable_id || 
                         notificationData.attributes.id;
        
        return {
          highlightedCommentId: commentId, // Use the specific comment ID from notification
          autoExpandComments: true,
          notificationData: notificationData,
          highlightPost: true // Also highlight the post for comment notifications
        };
      } else {
        // For other notifications (like, follow, etc.), highlight the post
        return {
          highlightedCommentId: undefined,
          autoExpandComments: false,
          notificationData: undefined,
          highlightPost: true // Highlight the post for non-comment notifications
        };
      }
    }
    
    return { highlightedCommentId: undefined, autoExpandComments: false, highlightPost: false };
  };

  const scrollToRow = (id: any) => {
    const index = posts.findIndex((item: any) => parseInt(item.id) === id);
    if (index !== -1 && listContainerRef.current && listContainerRef.current.children[index]) {
      const rowElement = listContainerRef.current.children[index];
      centerElementInContainer(listContainerRef.current, rowElement as HTMLElement);
      setHighlightedPostId(id);

      // Clear post highlight after 25 seconds
      setTimeout(() => {
        setHighlightedPostId(null);
      }, 25000);
    }
  };

  useEffect(() => {
    if(posts.length && scrollToPost > 0){
      scrollToRow(scrollToPost)
    }
  }, [posts]);

  useEffect(() => {
    const hasMore = pageNo < totalPages
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !postLoader && hasMore) {
          handlePageNoChange(pageNo + 1)
        }
      },
      {
        threshold: 0.1,
        rootMargin: '300px'
      }
    );

    if (thresholdRef.current) {
      observer.observe(thresholdRef.current);
    }

    return () => {
      if (thresholdRef.current) {
        observer.unobserve(thresholdRef.current);
      }
    };
  }, [pageNo, totalPages, postLoader])

  useEffect(() => {
    const intervalId = setInterval(()=> {
      handlePageNoChange(1)
    },300000);
  
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div>
      {!isFullScreenMode && (
        <Grid item className={classes.createPostContainer}>
          <CreateNewPost userProfile={userAccount} handleCreatePostRedirection={handleCreatePostRedirection} />
        </Grid>
      )}
      <Container className={classes.contantFeedContainer}>
        <Grid container direction="column" className={classes.root}>

          <CustomLoader wrapperHeight="20vh" isLoading={isFirstPageLoading}>
            <PostWrapper ref={listContainerRef} className={classes.allPosts}>
              {posts.length > 0 &&
                posts.map((post) => {
                  const notificationProps = getPostNotificationProps(post);
                  const isHighlighted = notificationProps.highlightPost ? (highlightedPostId === post.id) : false;

                  // console.log("Post highlighting check:", {
                  //   postId: post.id,
                  //   highlightedPostId,
                  //   highlightPost: notificationProps.highlightPost,
                  //   isHighlighted,
                  //   notificationProps
                  // });

                  return (
                    <Box
                      key={post.id}
                      className={classes.postItem}
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        marginBottom: "0px",
                        paddingBottom: "0px"
                      }}
                      data-post-id={posts.indexOf(post)}
                    >
                      <Post
                        postInfo={post}
                        allPosts={posts}
                        achiveId={achiveId}
                        showAlert={showAlert}
                        showSuccess={showSuccess}
                        userAccount={userAccount}
                        errorAlertMessage={errorAlertMessage}
                        isSortingComments={isSortingComments}
                        highlighted={isHighlighted}
                        banUserApiCall={banUserApiCall}
                        hidePostApicall={hidePostApicall}
                        deletePostApicall={deletePostApicall}
                        postCommentApiCall={postCommentApiCall}
                        postPollApiCall={postPollApiCall}
                        deleteCommentApiCall={deleteCommentApiCall}
                        archiveCommentApiCall={archiveCommentApiCall}
                        handleLikePostAPICall={handleLikePostAPICall}
                        handleUndoLikeAPICall={handleUndoLikeAPICall}
                        getPostCommentsApiCall={getPostCommentsApiCall}
                        revertArchiveCommentApiCall={revertArchiveCommentApiCall}
                        handlePostProfileClick={handlePostProfileClick}
                        handleSortingCommentsChange={handleSortingCommentsChange}
                        handleVote={handleVote}
                        handleDeleteVote={handleDeleteVote}
                        handleVoteComment={handleVoteComment}
                        highlightedCommentId={notificationProps.highlightedCommentId}
                        autoExpandComments={notificationProps.autoExpandComments}
                        notificationData={notificationProps.notificationData}
                        setCommentModalOpen={setCommentModalOpen}
                      />
                    </Box>
                  );
                })}
              {pageNo < totalPages && (
                <div ref={thresholdRef}>
                  <CustomLoader loaderSize={24} isLoading={postLoader} />
                </div>
              )}
            </PostWrapper>
          </CustomLoader>
        </Grid>
      </Container>
    </div>
  );
}

export default ContentFeed;
// Customizable Area End
